import _ from "lodash";
import * as Elements from "./elements.js";

function init(wpd, wpdDocument, dataEntryVM) {
	wpd.xyGraphEdit = {};

	let calibrator = null;

	const elementIds = {
		editXYGraphSidebar: "edit-xy-graph-sidebar",
		editXYGraphAdjustPointsSidebar: "edit-xy-graph-adjust-points-sidebar",
		xyGraphAlignAxesSidebar: "xy-graph-align-axes-sidebar",
		cancelXyGraphCalibrationDialog: "cancel-xy-graph-calibration-dialog",
		calibrateAxisValuesSidebar: "calibrate-xy-graph-axis-values-sidebar",
		calibrateAxisDataTypesSidebar: "calibrate-xy-graph-axis-data-types-sidebar",
		calibrateAxisStudyBranchesSidebar:
			"calibrate-xy-graph-axis-study-branches-sidebar",
	};

	Elements.apply(wpd, wpdDocument, elementIds, dataEntryVM);

	const showAxisPoints = function () {
		const axis = wpd.tree.getActiveAxes();
		const calibration = axis.calibration;
		calibration.unselectAll();
		calibrator = new wpd.XYAxesCalibrator(calibration, true);
		calibrator.reload();

		wpd.graphicsWidget.setRepainter(
			new wpd.AlignmentCornersRepainter(calibration),
		);

		wpd.graphicsWidget.forceHandlerRepaint();
	};

	const hideAxisPoints = function () {
		const axis = wpd.tree.getActiveAxes();
		const calibration = axis.calibration;
		calibration.unselectAll();

		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.resetData();
		wpd.graphicsWidget.forceHandlerRepaint();
	};

	// Populate the values in the axis point values popup
	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	const setAxisPointFieldValues = function (isSidebar) {
		const axis = wpd.tree.getActiveAxes();
		const calibration = axis.calibration;

		if (calibration && calibration.getCount() == 4) {
			// Use different element IDs based on whether we're editing or creating
			const prefix = isSidebar ? "xy-" : "xy-edit-";
			const noRotationId = isSidebar
				? "xy-axes-no-rotation"
				: "xy-edit-axes-no-rotation";
			wpdDocument.getElementById(prefix + "xmin").value =
				calibration.getPoint(0).dx;
			wpdDocument.getElementById(prefix + "xmax").value =
				calibration.getPoint(1).dx;
			wpdDocument.getElementById(prefix + "ymin").value =
				calibration.getPoint(2).dy;
			wpdDocument.getElementById(prefix + "ymax").value =
				calibration.getPoint(3).dy;
			wpdDocument.getElementById(prefix + "xlog").checked = axis.isLogX();
			wpdDocument.getElementById(prefix + "ylog").checked = axis.isLogY();
			wpdDocument.getElementById(noRotationId).checked = axis.noRotation();
		}
	};

	const showError = function (containerClass, errorClass) {
		wpdDocument
			.getElementById(containerClass)
			.querySelector(`.${errorClass}`)
			.removeAttribute("hidden");
	};

	const hideError = function (containerClass, errorClass) {
		wpdDocument
			.getElementById(containerClass)
			.querySelector(`.${errorClass}`)
			.setAttribute("hidden", "true");
	};

	// Save updates to axis point values
	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	const saveAxisPointValues = function (isSidebar) {
		if (isSidebar) {
			hideError(
				elementIds.calibrateAxisValuesSidebar,
				"xy-log-validation-message",
			);
			hideError(elementIds.calibrateAxisValuesSidebar, "xy-validation-message");
		} else {
			hideError("xy-graph-set-values-dialog", "xy-edit-log-validation-message");
			hideError("xy-graph-set-values-dialog", "xy-edit-validation-message");
		}

		// Use different element IDs based on whether we're editing or creating
		const prefix = isSidebar ? "xy-" : "xy-edit-";
		const noRotationId = isSidebar
			? "xy-axes-no-rotation"
			: "xy-edit-axes-no-rotation";
		const values = {
			xmin: wpdDocument.getElementById(prefix + "xmin").value,
			xmax: wpdDocument.getElementById(prefix + "xmax").value,
			ymin: wpdDocument.getElementById(prefix + "ymin").value,
			ymax: wpdDocument.getElementById(prefix + "ymax").value,
			xlog: wpdDocument.getElementById(prefix + "xlog").checked,
			ylog: wpdDocument.getElementById(prefix + "ylog").checked,
			noRotation: wpdDocument.getElementById(noRotationId).checked,
		};

		let axis = wpd.tree.getActiveAxes();
		let calibration = axis.calibration;

		if (
			(values.xlog &&
				(parseFloat(values.xmin) <= 0 || parseFloat(values.xmax) <= 0)) ||
			(values.ylog &&
				(parseFloat(values.ymin) <= 0 || parseFloat(values.ymax) <= 0))
		) {
			if (isSidebar) {
				showError(
					elementIds.calibrateAxisValuesSidebar,
					"xy-log-validation-message",
				);
			} else {
				showError(
					"xy-graph-set-values-dialog",
					"xy-edit-log-validation-message",
				);
			}
			return false;
		}

		calibration.setDataAt(0, values.xmin, values.ymin);
		calibration.setDataAt(1, values.xmax, values.ymin);
		calibration.setDataAt(2, values.xmin, values.ymin);
		calibration.setDataAt(3, values.xmax, values.ymax);
		const calibrateResult = axis.calibrate(
			calibration,
			values.xlog,
			values.ylog,
			values.noRotation,
		);

		if (!calibrateResult) {
			if (isSidebar) {
				showError(
					elementIds.calibrateAxisValuesSidebar,
					"xy-validation-message",
				);
			} else {
				showError("xy-graph-set-values-dialog", "xy-edit-validation-message");
			}
			return;
		}

		return values;
	};

	const updateGraphPointWithAxisPointValues = function (
		axis,
		dataset,
		dataPoint,
		skipUpdateAxis,
		decimalOnly,
	) {
		const metadata = axis.getMetadata();
		const axisValues = axis.pixelToData(dataPoint.x, dataPoint.y);
		let isUpdated = false;
		Object.keys(dataPoint.metadata)
			.filter(
				(key) => !isNaN(key) && !_.isNil(dataPoint.metadata[key].axisType),
			)
			.forEach((key) => {
				const row = dataPoint.metadata[key];
				if (
					row.axis === "x" &&
					row.axisType === "value" &&
					skipUpdateAxis !== "x"
				) {
					row.value = wpd.utils.roundAxisValue(
						decimalOnly ? row.value : axisValues[0],
						axis.isLogX(),
						metadata?.axesLabelData?.x?.decimals ?? 2,
					);
					isUpdated = true;
				} else if (
					row.axis === "y" &&
					row.axisType === "value" &&
					skipUpdateAxis !== "y"
				) {
					row.value = wpd.utils.roundAxisValue(
						decimalOnly ? row.value : axisValues[1],
						axis.isLogY(),
						metadata?.axesLabelData?.y?.decimals ?? 2,
					);
					isUpdated = true;
				}
			});

		// If an update was made, regenerate the tuple id
		if (
			isUpdated &&
			dataEntryVM.pointGroupOverhaul &&
			dataset.hasPointGroups() &&
			dataPoint.metadata.pointGroupAxisDirection === "center"
		) {
			wpd.pointGroups.syncTupleIdsToCenterPoint(dataset, dataPoint);
		}
	};

	wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues = function (
		axis,
		dataset,
		pointIndexes,
		skipUpdateAxis,
		decimalOnly,
	) {
		if (!axis) {
			axis = wpd.tree.getActiveAxes();
		}

		if (dataset && pointIndexes && pointIndexes.length > 0) {
			pointIndexes.forEach((index) => {
				const dataPoint = dataset.getPixel(index);
				updateGraphPointWithAxisPointValues(
					axis,
					dataset,
					dataPoint,
					skipUpdateAxis,
					decimalOnly,
				);
			});
		} else {
			const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

			datasets.forEach((dataset) => {
				const dataPoints = dataset.getAllPixels();
				dataPoints.forEach((dataPoint) => {
					updateGraphPointWithAxisPointValues(
						axis,
						dataset,
						dataPoint,
						null,
						decimalOnly,
					);
				});
			});
		}
	};

	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	const setAxisDataTypeFieldValues = function (isSidebar) {
		let axis = wpd.tree.getActiveAxes();
		const metadata = axis.getMetadata();
		const xData = metadata?.axesLabelData?.x;
		const yData = metadata?.axesLabelData?.y;
		// Clear all error messages
		const errorMessages = wpdDocument
			.getElementById(
				isSidebar
					? "calibrate-xy-graph-axis-data-types-sidebar"
					: "graph-axes-label-popup",
			)
			.querySelectorAll(".error-message");
		errorMessages.forEach((msg) => (msg.textContent = ""));

		if (!xData || !yData) {
			if (!isSidebar) {
				console.error("XY graph missing axis data type metadata");
				return;
			} else {
				// Reset all fields to defaults when no metadata exists
				const prefix = "add-";
				wpdDocument.getElementById(prefix + "x-axis-data-type").value = "";
				wpdDocument.getElementById(prefix + "y-axis-data-type").value = "";
				wpdDocument.getElementById(prefix + "x-axis-unit").value = "";
				wpdDocument.getElementById(prefix + "y-axis-unit").value = "";
				wpdDocument.getElementById(prefix + "x-axis-description").value = "";
				wpdDocument.getElementById(prefix + "y-axis-description").value = "";
				wpdDocument.getElementById(prefix + "x-axis-decimals").value = "2";
				wpdDocument.getElementById(prefix + "y-axis-decimals").value = "2";

				wpd.alignAxes.destroyAwesompletes();
				wpd.alignAxes.initializeXyGraphFields(true);
				wpd.utils.initializeRatioTypesSelect(
					wpdDocument.getElementById("add-x-axis-change-from-baseline"),
				);
				wpd.utils.initializeRatioTypesSelect(
					wpdDocument.getElementById("add-y-axis-change-from-baseline"),
				);
				wpd.alignAxes.populateBroadcastTags([], true, "xy");
				return;
			}
		}

		const prefix = isSidebar ? "add-" : "";
		wpdDocument.getElementById(prefix + "x-axis-data-type").value =
			xData.dataType;
		wpdDocument.getElementById(prefix + "y-axis-data-type").value =
			yData.dataType;
		wpdDocument.getElementById(prefix + "x-axis-unit").value = xData.unit;
		wpdDocument.getElementById(prefix + "y-axis-unit").value = yData.unit;

		wpdDocument.getElementById(prefix + "x-axis-description").value =
			xData.description ?? "";
		wpdDocument.getElementById(prefix + "y-axis-description").value =
			yData.description ?? "";
		// initialize dropdowns and load saved values
		wpd.utils.initializeRatioTypesSelect(
			wpdDocument.getElementById(prefix + "x-axis-change-from-baseline"),
			xData.changeFromBaseline,
		);
		wpd.utils.initializeRatioTypesSelect(
			wpdDocument.getElementById(prefix + "y-axis-change-from-baseline"),
			yData.changeFromBaseline,
		);

		wpdDocument.getElementById(prefix + "x-axis-decimals").value =
			xData.decimals;
		wpdDocument.getElementById(prefix + "y-axis-decimals").value =
			yData.decimals;

		// load point group
		wpd.pointGroups.loadFromSavedSettings(axis);

		wpd.alignAxes.destroyAwesompletes();
		wpd.alignAxes.initializeXyGraphFields(isSidebar);

		// Populate the broadcast-tags section. Awesompletes for these rows are
		// created inside populateBroadcastTags, so this must run AFTER
		// destroyAwesompletes / initializeXyGraphFields.
		wpd.alignAxes.populateBroadcastTags(
			metadata.axesLabelData?.broadcastTags || [],
			isSidebar,
			"xy",
		);
	};

	wpd.xyGraphEdit.updateGraphPointsWithAxisDataTypes = function (
		axis,
		dataTypes,
	) {
		const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);
		const changeFromBaselineTag = _.find(wpd.tagGroups.tags, {
			name: "Change from baseline",
		});
		const descriptionTag = _.find(wpd.tagGroups.tags, {
			name: "Description",
		});
		const valueUnitTag = wpd.tagGroups.findTag({
			name: "Unit",
		});
		const timeUnitTag = wpd.tagGroups.findTag({
			name: "Time (unit)",
		});

		datasets.forEach((ds) => {
			const pixels = ds.getAllPixels();
			pixels.forEach((px, index) => {
				let maxIndex = 0;
				let reindex = false;
				let hasXDescription = false;
				let hasYDescription = false;
				let hasXChangeFromBaseline = false;
				let hasYChangeFromBaseline = false;
				let hasXUnit = false;
				let hasYUnit = false;

				let filteredMetadataKeys = Object.keys(px.metadata).filter(
					(key) => !isNaN(key),
				);

				if (filteredMetadataKeys.length === 0) {
					// need to calculate axis type via axis key and tag
					filteredMetadataKeys = Object.keys(px.metadata).filter(
						(key) => !isNaN(key) && !_.isNil(px.metadata[key].axis),
					);

					filteredMetadataKeys.forEach((key) => {
						switch (px.metadata[key].tag.uuid) {
							case changeFromBaselineTag.uuid:
								px.metadata[key].axisType = "changeFromBaseline";
								break;
							case valueUnitTag.uuid:
								px.metadata[key].axisType = "unit";
								break;
							case timeUnitTag.uuid:
								px.metadata[key].axisType = "unit";
								break;
							default:
								px.metadata[key].axisType = "value";
						}
					});
				}

				filteredMetadataKeys.forEach((key) => {
					const dp = px.metadata[key];
					maxIndex = Math.max(maxIndex, parseInt(key, 10));
					let isDelete = false;

					if (dp.axis === "x" && dp.axisType === "unit") {
						hasXUnit = true;
						dp.tag =
							dataTypes.xAxisDataType === "Time (number)"
								? timeUnitTag
								: valueUnitTag;

						dp.value = dataTypes.xAxisUnit;
					} else if (dp.axis === "x" && dp.axisType === "value") {
						if (ds.hasPointGroups("x")) {
							let tag;
							if (dataEntryVM.pointGroupOverhaul) {
								tag = wpd.pointGroups.getDataPointPointGroupTag(ds, index, "x");
							} else {
								tag = wpd.pointGroups.getDataPointPointGroupTagFromSettings(
									ds,
									"x",
									"xy",
									index,
								);
							}

							if (tag) {
								dp.tag = _.pick(tag, ["name", "uuid"]);
							} else {
								dp.tag = _.pick(
									_.find(wpd.tagGroups.tags, {
										name: dataTypes.xAxisDataType,
									}),
									["name", "uuid"],
								);
							}
						} else {
							dp.tag = _.pick(
								_.find(wpd.tagGroups.tags, {
									name: dataTypes.xAxisDataType,
								}),
								["name", "uuid"],
							);
						}

						dp.value = wpd.utils.roundAxisValue(
							dp.value,
							axis.isLogX(),
							dataTypes.xAxisDecimals,
						);
					} else if (dp.axis === "x" && dp.axisType === "changeFromBaseline") {
						hasXChangeFromBaseline = true;
						if (dataTypes.xAxisChangeFromBaseline) {
							dp.value = dataTypes.xAxisChangeFromBaseline;
						} else {
							isDelete = true;
							reindex = true;
							delete px.metadata[key];
						}
					} else if (dp.axis === "y" && dp.axisType === "unit") {
						hasYUnit = true;
						dp.tag =
							dataTypes.yAxisDataType === "Time (number)"
								? timeUnitTag
								: valueUnitTag;
						dp.value = dataTypes.yAxisUnit;
					} else if (dp.axis === "y" && dp.axisType === "value") {
						if (ds.hasPointGroups("y")) {
							let tag;
							if (dataEntryVM.pointGroupOverhaul) {
								tag = wpd.pointGroups.getDataPointPointGroupTag(ds, index, "y");
							} else {
								tag = wpd.pointGroups.getDataPointPointGroupTagFromSettings(
									ds,
									"y",
									"xy",
									index,
								);
							}

							if (tag) {
								dp.tag = _.pick(tag, ["name", "uuid"]);
							} else {
								dp.tag = _.pick(
									_.find(wpd.tagGroups.tags, {
										name: dataTypes.yAxisDataType,
									}),
									["name", "uuid"],
								);
							}
						} else {
							dp.tag = _.pick(
								_.find(wpd.tagGroups.tags, {
									name: dataTypes.yAxisDataType,
								}),
								["name", "uuid"],
							);
						}

						dp.value = wpd.utils.roundAxisValue(
							dp.value,
							axis.isLogY(),
							dataTypes.yAxisDecimals,
						);
					} else if (dp.axis === "y" && dp.axisType === "changeFromBaseline") {
						hasYChangeFromBaseline = true;
						if (dataTypes.yAxisChangeFromBaseline) {
							dp.value = dataTypes.yAxisChangeFromBaseline;
						} else {
							isDelete = true;
							reindex = true;
							delete px.metadata[key];
						}
					} else if (dp.axis === "x" && dp.axisType === "description") {
						hasXDescription = true;
						if (dataTypes.xAxisDescription) {
							dp.value = dataTypes.xAxisDescription;
						} else {
							isDelete = true;
							reindex = true;
							delete px.metadata[key];
						}
					} else if (dp.axis === "y" && dp.axisType === "description") {
						hasYDescription = true;
						if (dataTypes.yAxisDescription) {
							dp.value = dataTypes.yAxisDescription;
						} else {
							isDelete = true;
							reindex = true;
							delete px.metadata[key];
						}
					}

					if (!isDelete) {
						px.metadata[key] = dp;
					}
				});

				if (dataTypes.xAxisDescription && !hasXDescription) {
					px.metadata[++maxIndex] = {
						tag: descriptionTag,
						value: dataTypes.xAxisDescription,
						axis: "x",
						axisType: "description",
					};
				}
				if (dataTypes.yAxisDescription && !hasYDescription) {
					px.metadata[++maxIndex] = {
						tag: descriptionTag,
						value: dataTypes.yAxisDescription,
						axis: "y",
						axisType: "description",
					};
				}

				if (dataTypes.xAxisChangeFromBaseline && !hasXChangeFromBaseline) {
					px.metadata[++maxIndex] = {
						tag: changeFromBaselineTag,
						value: dataTypes.xAxisChangeFromBaseline,
						axis: "x",
						axisType: "changeFromBaseline",
					};
				}
				if (dataTypes.yAxisChangeFromBaseline && !hasYChangeFromBaseline) {
					px.metadata[++maxIndex] = {
						tag: changeFromBaselineTag,
						value: dataTypes.yAxisChangeFromBaseline,
						axis: "y",
						axisType: "changeFromBaseline",
					};
				}

				if (dataTypes.xAxisUnit && !hasXUnit) {
					px.metadata[++maxIndex] = {
						tag:
							dataTypes.xAxisDataType === "Time (number)"
								? timeUnitTag
								: valueUnitTag,
						value: dataTypes.xAxisUnit,
						axis: "x",
						axisType: "unit",
					};
				}
				if (dataTypes.yAxisUnit && !hasYUnit) {
					px.metadata[++maxIndex] = {
						tag:
							dataTypes.yAxisDataType === "Time (number)"
								? timeUnitTag
								: valueUnitTag,
						value: dataTypes.yAxisUnit,
						axis: "y",
						axisType: "unit",
					};
				}

				if (reindex) {
					wpd.custom.reindexPixelMetadata(px);
				}
			});

			// regenerate tuple data
			if (dataEntryVM.pointGroupOverhaul && ds.hasPointGroups()) {
				wpd.pointGroups
					.sortDataPointsByCenterFirst(axis, ds.getAllPixels())
					.forEach((px) => {
						let axisDirection = px.metadata.pointGroupAxisDirection;

						if (!axisDirection) {
							axisDirection = "center";
							px.metadata.pointGroupAxisDirection = axisDirection;
						}

						px.metadata.tuple = wpd.pointGroups.generateTupleData(
							ds,
							px,
							axisDirection,
						);
					});
			}
		});
	};

	//TODO: remove isSidebar flag when reworkXYGraphCalibration is deprecated
	const setStudyBranchFieldValues = function (isSidebar) {
		let axis = wpd.tree.getActiveAxes();
		const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);

		const prefix = isSidebar ? "add-" : "";
		const rowContainer = wpdDocument.getElementById(
			prefix + "axis-study-branch-inputs",
		);

		if (_.isEmpty(datasets)) {
			// reset to default state
			if (isSidebar) {
				const rows = Array.from(rowContainer.children);

				for (let i = rows.length - 1; i > 0; i--) {
					rows[i].remove();
				}

				if (rows.length > 0) {
					const firstRow = rows[0];
					const dsInput = firstRow.querySelector(
						".add-dataset-study-arm-input",
					);
					const tgInput = firstRow.querySelector(
						".add-dataset-tag-group-input",
					);
					const errorMessages = firstRow.querySelectorAll(
						".error-message, .uneditable-message",
					);

					if (dsInput) {
						dsInput.value = "";
						dsInput.disabled = false;
						dsInput.removeAttribute("data-composite-key");
					}
					if (tgInput) {
						tgInput.value = "";
						tgInput.disabled = false;
					}
					errorMessages.forEach((msg) => (msg.textContent = ""));
				}
			}

			return;
		}

		for (let i = 0; i < datasets.length - 1; i++) {
			wpd.dataSeriesManagement.addRow(false, isSidebar);
		}

		const rows = Array.from(rowContainer.children);
		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			const ds = datasets[i];
			const md = ds.getMetadata();
			const dsInput = row.querySelector(".add-dataset-study-arm-input");
			const tgInput = row.querySelector(".add-dataset-tag-group-input");
			dsInput.setAttribute("data-composite-key", md.studyData.compositeKey);
			dsInput.value = wpd.CompositeKeyUtils.getCompositeKeyValue(
				md.studyData.compositeKey,
			);
			tgInput.value = md.studyData.tagGroup.name;
			// align row's dataset uuid with the existing dataset so edit-mode
			// rows reuse the committed identity instead of the freshly minted one
			row.dataset.datasetUuid = md.uuid;

			if (datasets[i].getAllPixels().length > 0) {
				dsInput.disabled = true;
				tgInput.disabled = true;
				const removeButton = row.querySelector(".dataset-remove-button");

				if (removeButton) {
					removeButton.disabled = true;
				}

				const errorContainer = row.querySelector(".uneditable-message");
				errorContainer.innerText =
					"This study branch has data points and cannot be edited/removed from this tool; use the edit/remove buttons in the Graph Data window instead";
			}
		}
	};

	wpd.xyGraphEdit.zoomCalibrationPoint = function (index) {
		const axis = wpd.tree.getActiveAxes();
		const calibration = axis.calibration;
		var point = calibration.getPoint(index);
		wpd.graphicsWidget.updateZoomToImagePosn(point.px, point.py);
	};

	// Adjusting axis point positions
	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.adjustAxisPoints = function () {
		showAxisPoints();
		wpd.sidebar.show("edit-xy-graph-adjust-points-sidebar");
	};

	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.completeAdjustAxisPoints = function () {
		const axis = wpd.tree.getActiveAxes();
		axis.calibrate(
			axis.calibration,
			axis.isLogX(),
			axis.isLogY(),
			axis.noRotation(),
		);
		wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues();
		hideAxisPoints();
		wpd.graphicsWidget.setTool(
			new wpd.XYGraphPointTool(wpd.tree.getActiveAxes()),
		);
		wpd.sidebar.clear();
		wpd.sidebar.show("edit-xy-graph-sidebar");
	};

	// Setting axis point values
	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.openSetAxisPointValuesDialog = function () {
		setAxisPointFieldValues(false);
		wpd.popup.show("xy-graph-set-values-dialog");
	};

	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.closeSetAxisPointValuesDialog = function () {
		wpd.popup.close("xy-graph-set-values-dialog");
	};

	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.confirmSetAxisPointValues = function () {
		const axis = wpd.tree.getActiveAxes();

		const p1ValueBefore = axis.calibration.getPoint(0).dy;
		const p2ValueBefore = axis.calibration.getPoint(1).dy;
		const p3ValueBefore = axis.calibration.getPoint(2).dy;
		const p4ValueBefore = axis.calibration.getPoint(3).dy;
		const isLogXBefore = axis.isLogX();
		const isLogYBefore = axis.isLogY();
		const noRotationBefore = axis.noRotation();

		const saveResult = saveAxisPointValues(false);

		if (!saveResult) return;

		const p1ValueAfter = axis.calibration.getPoint(0).dy;
		const p2ValueAfter = axis.calibration.getPoint(1).dy;
		const p3ValueAfter = axis.calibration.getPoint(2).dy;
		const p4ValueAfter = axis.calibration.getPoint(3).dy;
		const isLogXAfter = axis.isLogX();
		const isLogYAfter = axis.isLogY();
		const noRotationAfter = axis.noRotation();

		// Recalculate point values when any axis point value changes, or when a
		// log-scale or rotation setting changes. Toggling log or rotation re-maps
		// every pixel to a different value even if the calibration point values
		// are unchanged, so the stored values must be re-derived.
		if (
			p1ValueBefore !== p1ValueAfter ||
			p2ValueBefore !== p2ValueAfter ||
			p3ValueBefore !== p3ValueAfter ||
			p4ValueBefore !== p4ValueAfter ||
			isLogXBefore !== isLogXAfter ||
			isLogYBefore !== isLogYAfter ||
			noRotationBefore !== noRotationAfter
		) {
			wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues();
		}

		wpd.popup.close("xy-graph-set-values-dialog");
	};

	// Setting axis data types
	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.openSetDataTypesDialog = function () {
		setAxisDataTypeFieldValues(false);

		wpdDocument
			.getElementById("xy-edit-graph-axis-buttons")
			.removeAttribute("hidden");
		wpdDocument
			.getElementById("xy-graph-axis-buttons")
			.setAttribute("hidden", "true");

		wpd.popup.show("graph-axes-label-popup");
	};

	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.closeSetDataTypesDialog = function () {
		wpd.popup.close("graph-axes-label-popup");
		wpd.alignAxes.clearAxesLabelValues();
		wpd.alignAxes.destroyAwesompletes();
		wpd.pointGroups.resetSettings();
	};

	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.confirmSetDataTypes = async function () {
		const axis = wpd.tree.getActiveAxes();
		const xAxisDecimalsBefore = axis.getMetadata().axesLabelData?.x?.decimals;
		const yAxisDecimalsBefore = axis.getMetadata().axesLabelData?.y?.decimals;

		const saveResult = wpd.alignAxes.saveAxesData(false, false, true);
		if (!saveResult) return;

		const xAxisDecimalsAfter = saveResult.xAxisDecimals;
		const yAxisDecimalsAfter = saveResult.yAxisDecimals;

		wpd.xyGraphEdit.updateGraphPointsWithAxisDataTypes(axis, saveResult);

		await wpd.alignAxes.cascadeBroadcastTagsChange(
			axis,
			saveResult.broadcastTagsBefore,
			saveResult.broadcastTagsAfter,
		);

		if (
			xAxisDecimalsBefore !== xAxisDecimalsAfter ||
			yAxisDecimalsBefore !== yAxisDecimalsAfter
		) {
			wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues(
				null,
				null,
				null,
				null,
				true,
			);
		}

		wpd.popup.close("graph-axes-label-popup");
		wpd.graphicsWidget.removeTool();
		wpd.tree.editAxis(axis.name);
	};

	//TODO: remove when reworkXYGraphCalibration is deprecated
	// Setting study branches
	wpd.xyGraphEdit.openSetStudyBranchesDialog = function () {
		wpd.dataSeriesManagement.showAddDataset(true);
		setStudyBranchFieldValues(false);

		// build per-branch broadcast groups from the rows just populated, then
		// fill in any saved broadcast entries from axesLabelData
		wpd.dataSeriesManagement.syncBranchBroadcastSection();
		const axes = wpd.tree.getActiveAxes();
		const existing = axes?.getMetadata?.()?.axesLabelData?.broadcastTags;
		wpd.dataSeriesManagement.populateBranchBroadcastTags(existing);
	};

	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.closeSetStudyBranchesDialog = function () {
		wpd.dataSeriesManagement.resetDialog();
		wpd.popup.close("add-dataset-popup");
	};

	//TODO: remove when reworkXYGraphCalibration is deprecated
	wpd.xyGraphEdit.confirmSetStudyBranches = function () {
		const rowContainer = wpdDocument.getElementById("axis-study-branch-inputs");

		const rows = Array.from(rowContainer.children);
		const studyBranchKeys = rows
			.map((row) => row.querySelector(".add-dataset-study-arm-input"))
			.filter((input) => input.value !== "")
			.map((input) => input.getAttribute("data-composite-key"));
		const tagGroupNames = rows
			.map((row) => row.querySelector(".add-dataset-tag-group-input"))
			.filter((input) => input.value !== "")
			.map((input) => input.value);

		let axis = wpd.tree.getActiveAxes();
		const datasets = wpd.appData.getPlotData().getDatasetsForAxis(axis);
		let existingKeys = new Set();

		// if composite key is not in UI list, delete dataset
		// if composite key is in UI list, check tag group
		datasets.forEach((ds) => {
			const md = ds.getMetadata();
			existingKeys.add(md.studyData.compositeKey);
			if (!studyBranchKeys.includes(md.studyData.compositeKey)) {
				wpd.appData.getPlotData().deleteDataset(ds);
				wpd.tree.refreshPreservingSelection();
			} else {
				const index = studyBranchKeys.findIndex(
					(key) => key === md.studyData.compositeKey,
				);
				md.studyData.tagGroup.name = tagGroupNames[index];
			}
		});

		for (let i = 0; i < studyBranchKeys.length; i++) {
			// if composite key is not in the MD list, add it
			if (!existingKeys.has(studyBranchKeys[i])) {
				wpd.dataSeriesManagement.saveDataset(false, rows[i]);
			}
		}

		// write per-branch broadcast tags. Branches removed from the dialog
		// are dropped naturally because their groups are not collected; the
		// merge with graph-wide entries preserves DCTL-1655 defaults.
		wpd.dataSeriesManagement.writeBranchBroadcastTagsToActiveAxes();

		wpd.tree.editAxis(axis.name);
		wpd.popup.close("add-dataset-popup");
		wpd.dataSeriesManagement.resetDialog(false);
	};

	wpd.xyGraphEdit.closeXYGraphEdit = function () {
		hideAxisPoints();
		wpd.sidebar.clear();
	};

	wpd.xyGraphEdit.confirmGraphType = function () {
		const selectedGraphType = wpdDocument
			.getElementById("axesList")
			.querySelector("input[type='radio']:checked")
			?.getAttribute("id");

		const axesTypeString = selectedGraphType === "r_xy" ? "xy" : "bar";

		const calibration = new wpd.Calibration(2);
		calibration.labels = ["X1", "X2", "Y1", "Y2"];
		calibration.labelPositions = ["N", "N", "E", "E"];
		calibration.maxPointCount = 4;

		const calibrator = new wpd.XYAxesCalibrator(calibration);
		if (calibrator != null) {
			wpd.tree.selectPath("/" + wpd.gettext("axes"));
			wpd.graphicsWidget.setRepainter(
				new wpd.AlignmentCornersRepainter(calibration, axesTypeString),
			);

			let axes = new wpd.XYAxes();
			axes.calibrate(calibration, false, false, false);
			axes.name = wpd.alignAxes.makeAxesName(wpd.XYAxes);

			const md = axes.getMetadata();
			md.wizardStep = "defineAxisPoints";
			axes.setMetadata(md);

			let plot = wpd.appData.getPlotData();
			plot.addAxes(axes, wpd.appData.isMultipage());
			wpd.alignAxes.postProcessAxesAdd(axes);

			wpd.tree.refreshPreservingSelection();
			wpd.tree.editAxis(axes.name);
		}

		wpd.popup.close("axesList");
		const nextButton = wpdDocument.getElementById(
			"xy-graph-add-align-axes-complete-button",
		);
		nextButton.disabled = true;
	};

	wpd.xyGraphEdit.startAxisPointsCalibration = function (isEdit) {
		const axis = wpd.tree.getActiveAxes();
		if (!axis || wpd.custom.identifyAxis(axis) !== "xy") {
			return;
		}

		const metadata = axis.getMetadata();
		metadata.wizardStep = "defineAxisPoints";
		axis.setMetadata(metadata);

		const calibration = axis.calibration;

		calibration.unselectAll();
		calibrator = new wpd.XYAxesCalibrator(calibration, true);
		calibrator.reload();

		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.setRepainter(
			new wpd.AlignmentCornersRepainter(calibration),
		);

		wpd.graphicsWidget.forceHandlerRepaint();
		wpd.graphicsWidget.setTool(new wpd.AxesCornersTool(calibration));

		if (isEdit) {
			wpdDocument
				.getElementById("xy-graph-edit-align-axes-sidebar-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("xy-graph-add-align-axes-sidebar-buttons")
				.setAttribute("hidden", "true");
		} else {
			wpdDocument
				.getElementById("xy-graph-add-align-axes-sidebar-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("xy-graph-edit-align-axes-sidebar-buttons")
				.setAttribute("hidden", "true");
		}

		wpd.sidebar.show(elementIds.xyGraphAlignAxesSidebar);
	};

	wpd.xyGraphEdit.completeAxisPointsCalibration = function (isEdit) {
		if (isEdit) {
			wpd.sidebar.clear();
			wpd.graphicsWidget.removeTool();
			wpd.graphicsWidget.removeRepainter();
			wpd.graphicsWidget.resetData();
			wpd.graphicsWidget.forceHandlerRepaint();

			const activeAxis = wpd.tree.getActiveAxes();
			if (activeAxis) {
				wpd.tree.editAxis(activeAxis.name);
			}
		} else {
			wpd.xyGraphEdit.startDefineAxisValues(false);
		}
	};

	wpd.xyGraphEdit.showXyAxesInfoPopup = function () {
		wpd.popup.show("xyAxesInfo");
	};

	wpd.xyGraphEdit.closeXyAxesInfoPopup = function () {
		wpd.popup.close("xyAxesInfo");
	};

	wpd.xyGraphEdit.enableDefineAxisPointsButton = function () {
		wpdDocument.getElementById(
			"xy-graph-add-align-axes-complete-button",
		).disabled = false;
	};

	wpd.xyGraphEdit.startDefineAxisValues = function (isEdit) {
		wpd.graphicsWidget.removeTool();

		const activeAxis = wpd.tree.getActiveAxes();
		const calibration = activeAxis.calibration;
		calibration.unselectAll();

		// Reset fields
		wpdDocument.getElementById("xy-xmin").value = 0;
		wpdDocument.getElementById("xy-xmax").value = 1;
		wpdDocument.getElementById("xy-ymin").value = 0;
		wpdDocument.getElementById("xy-ymax").value = wpdDocument.getElementById(
			"xy-ymax",
		).value = 1;
		wpdDocument.getElementById("xy-xlog").checked = false;
		wpdDocument.getElementById("xy-ylog").checked = false;
		wpdDocument.getElementById("xy-axes-no-rotation").checked = true;

		const metadata = activeAxis.getMetadata();
		const calibrationStarted = metadata.calibrationStarted;
		if (calibrationStarted) {
			setAxisPointFieldValues(true);
		}

		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.setRepainter(
			new wpd.AlignmentCornersRepainter(calibration),
		);
		wpd.graphicsWidget.forceHandlerRepaint();

		metadata.wizardStep = "defineAxisValues";
		activeAxis.setMetadata(metadata);

		if (isEdit) {
			wpdDocument
				.getElementById("xy-graph-edit-axis-values-sidebar-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("xy-graph-add-axis-values-sidebar-buttons")
				.setAttribute("hidden", "true");
		} else {
			wpdDocument
				.getElementById("xy-graph-add-axis-values-sidebar-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("xy-graph-edit-axis-values-sidebar-buttons")
				.setAttribute("hidden", "true");
		}

		wpd.sidebar.clear();
		wpd.sidebar.show("calibrate-xy-graph-axis-values-sidebar");
	};

	wpd.xyGraphEdit.completeDefineAxisValues = function (isEdit) {
		const saveResult = saveAxisPointValues(true);
		if (!saveResult) return;

		if (isEdit) {
			wpd.sidebar.clear();
			wpd.graphicsWidget.removeTool();
			wpd.graphicsWidget.removeRepainter();
			wpd.graphicsWidget.resetData();
			wpd.graphicsWidget.forceHandlerRepaint();

			const activeAxis = wpd.tree.getActiveAxes();
			if (activeAxis) {
				wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues();
				wpd.tree.editAxis(activeAxis.name);
			}
		} else {
			wpd.xyGraphEdit.startDefineAxisDataTypes(false);
		}
	};

	wpd.xyGraphEdit.startDefineAxisDataTypes = function (isEdit) {
		const activeAxis = wpd.tree.getActiveAxes();
		const calibration = activeAxis.calibration;
		calibration.unselectAll();

		const md = activeAxis.getMetadata();
		md.wizardStep = "defineAxisDataTypes";
		md.calibrationStarted = true;
		activeAxis.setMetadata(md);

		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.setRepainter(
			new wpd.AlignmentCornersRepainter(calibration),
		);
		wpd.graphicsWidget.forceHandlerRepaint();

		setAxisDataTypeFieldValues(true);

		if (isEdit) {
			wpdDocument
				.getElementById("xy-graph-edit-data-types-sidebar-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("xy-graph-add-data-types-sidebar-buttons")
				.setAttribute("hidden", "true");
		} else {
			wpdDocument
				.getElementById("xy-graph-add-data-types-sidebar-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("xy-graph-edit-data-types-sidebar-buttons")
				.setAttribute("hidden", "true");
		}

		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.calibrateAxisDataTypesSidebar);
	};

	wpd.xyGraphEdit.completeDefineAxisDataTypes = async function (isEdit) {
		const saveResult = wpd.alignAxes.saveAxesData(true, false, true);
		if (!saveResult) return;

		if (isEdit) {
			wpd.sidebar.clear();
			wpd.graphicsWidget.removeTool();
			wpd.graphicsWidget.removeRepainter();
			wpd.graphicsWidget.resetData();
			wpd.graphicsWidget.forceHandlerRepaint();

			const activeAxis = wpd.tree.getActiveAxes();
			if (activeAxis) {
				wpd.xyGraphEdit.updateGraphPointsWithAxisDataTypes(
					activeAxis,
					saveResult,
				);
				await wpd.alignAxes.cascadeBroadcastTagsChange(
					activeAxis,
					saveResult.broadcastTagsBefore,
					saveResult.broadcastTagsAfter,
				);
				wpd.xyGraphEdit.updateGraphPointsWithAxisPointValues();
				wpd.tree.editAxis(activeAxis.name);
			}
		} else {
			wpd.xyGraphEdit.startDefineAxisStudyBranches(false);
		}
	};

	wpd.xyGraphEdit.startDefineAxisStudyBranches = function (isEdit) {
		const activeAxis = wpd.tree.getActiveAxes();
		const calibration = activeAxis.calibration;
		calibration.unselectAll();

		const md = activeAxis.getMetadata();
		md.wizardStep = "defineAxisStudyBranches";
		md.calibrationStarted = true;
		activeAxis.setMetadata(md);

		wpd.graphicsWidget.removeRepainter();
		wpd.graphicsWidget.setRepainter(
			new wpd.AlignmentCornersRepainter(calibration),
		);
		wpd.graphicsWidget.forceHandlerRepaint();

		wpd.dataSeriesManagement.initializeAutocompletes(true);
		setStudyBranchFieldValues(true);

		if (isEdit) {
			wpdDocument
				.getElementById("xy-graph-edit-study-branches-sidebar-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("xy-graph-add-study-branches-sidebar-buttons")
				.setAttribute("hidden", "true");
		} else {
			wpdDocument
				.getElementById("xy-graph-add-study-branches-sidebar-buttons")
				.removeAttribute("hidden");
			wpdDocument
				.getElementById("xy-graph-edit-study-branches-sidebar-buttons")
				.setAttribute("hidden", "true");
		}

		wpd.sidebar.clear();
		wpd.sidebar.show(elementIds.calibrateAxisStudyBranchesSidebar);
	};

	wpd.xyGraphEdit.completeDefineAxisStudyBranches = function (isEdit) {
		const isValid = wpd.dataSeriesManagement.validateAll(false);
		if (!isValid) {
			return;
		}

		const activeAxis = wpd.tree.getActiveAxes();
		const calibration = activeAxis.calibration;
		calibration.unselectAll();

		if (isEdit) {
			const rowContainer = wpdDocument.getElementById(
				"add-axis-study-branch-inputs",
			);

			const rows = Array.from(rowContainer.children);
			const studyBranchKeys = rows
				.map((row) => row.querySelector(".add-dataset-study-arm-input"))
				.filter((input) => input.value !== "")
				.map((input) => input.getAttribute("data-composite-key"));
			const tagGroupNames = rows
				.map((row) => row.querySelector(".add-dataset-tag-group-input"))
				.filter((input) => input.value !== "")
				.map((input) => input.value);

			const datasets = wpd.appData.getPlotData().getDatasetsForAxis(activeAxis);
			let existingKeys = new Set();

			// if composite key is not in UI list, delete dataset
			// if composite key is in UI list, check tag group
			datasets.forEach((ds) => {
				const md = ds.getMetadata();
				existingKeys.add(md.studyData.compositeKey);
				if (!studyBranchKeys.includes(md.studyData.compositeKey)) {
					wpd.appData.getPlotData().deleteDataset(ds);
					wpd.tree.refreshPreservingSelection();
				} else {
					const index = studyBranchKeys.findIndex(
						(key) => key === md.studyData.compositeKey,
					);
					md.studyData.tagGroup.name = tagGroupNames[index];
				}
			});

			for (let i = 0; i < studyBranchKeys.length; i++) {
				// if composite key is not in the MD list, add it
				if (!existingKeys.has(studyBranchKeys[i])) {
					wpd.dataSeriesManagement.saveDataset(false, rows[i]);
				}
			}

			wpd.tree.editAxis(activeAxis.name);
		} else {
			wpd.dataSeriesManagement.addDatasets(true);
			const md = activeAxis.getMetadata();
			md.calibrationComplete = true;
			activeAxis.setMetadata(md);

			wpd.graphicsWidget.removeTool();
			wpd.graphicsWidget.removeRepainter();
			wpd.graphicsWidget.resetData();
			wpd.graphicsWidget.forceHandlerRepaint();
			wpd.sidebar.clear();
		}
	};

	wpd.xyGraphEdit.cancelXyGraphCalibration = function () {
		wpd.sidebar.clear();
		wpd.graphicsWidget.removeTool();
		wpd.graphicsWidget.removeRepainter();

		const activeAxis = wpd.tree.getActiveAxes();
		if (activeAxis) {
			wpd.tree.deleteAxis(activeAxis.name, true);
		}

		wpd.graphicsWidget.resetData();
		wpd.graphicsWidget.forceHandlerRepaint();

		const graphAddButton = wpdDocument.getElementById("graph-add-button");
		graphAddButton.classList.remove("pressed-button");
		wpd.popup.close(elementIds.cancelXyGraphCalibrationDialog);
	};

	wpd.xyGraphEdit.showCancelXyGraphCalibrationDialog = function () {
		wpd.popup.show(elementIds.cancelXyGraphCalibrationDialog);
	};

	wpd.xyGraphEdit.closeCancelXyGraphCalibrationDialog = function () {
		wpd.popup.close(elementIds.cancelXyGraphCalibrationDialog);
	};
}

export { init };
